"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.trimTrailing = trimTrailing;
exports.toReasonableFileName = toReasonableFileName;
exports.partition = partition;
exports.threeWaySplit = threeWaySplit;
exports.pluralize = pluralize;
exports.formatNumber = formatNumber;
const constructs_1 = require("./constructs");
function trimTrailing(s, suffix) {
    if (suffix.length === 0) {
        return s;
    }
    while (s.endsWith(suffix)) {
        s = s.slice(0, -suffix.length);
    }
    return s;
}
/**
 * Sanitizes a string such that it can be used as a file name.
 */
function toReasonableFileName(input) {
    return input
        .split('')
        .map(c => (c.match(ALLOWED_FILE_NAME_SYMBOLS) ? c : '_'))
        .join('');
}
const ALLOWED_FILE_NAME_SYMBOLS = /[a-zA-Z0-9_-]/;
function partition(input, ...predicates) {
    const ret = [];
    let i = 0;
    for (const p of predicates) {
        const start = i;
        while (i < input.length && p(input[i])) {
            ++i;
        }
        ret.push(input.slice(start, i));
    }
    if (i < input.length) {
        throw new Error(`The input string could not be fully partitioned (remainder: "${input.slice(i, i + 100)}")`);
    }
    return ret;
}
function threeWaySplit(input, prefixPredicate, suffixPredicate) {
    let i = 0;
    while (i < input.length && prefixPredicate(input[i])) {
        ++i;
    }
    let j = input.length - 1;
    while (j >= i && suffixPredicate(input[j])) {
        --j;
    }
    return {
        prefix: input.slice(0, i),
        mid: input.slice(i, j + 1),
        suffix: input.slice(j + 1),
    };
}
function pluralize(s, occurrences) {
    return occurrences === 1 ? s : s + 's';
}
/**
 * Formats a number to a more readable string representation with units or commas.
 * For example, with "POSTFIX" mode, 1500 will be converted to '1.5K', 1000 to '1K'.
 * With "COMMAS" mode, 1500 will be converted to '1,500'.
 * Works until trillions.
 *
 * @param {number} num - The number to format.
 * @param {FormatMode} mode - The formatting mode: either "COMMAS" or "POSTFIX".
 * @returns {string} The formatted number as a string with units or commas.
 */
function formatNumber(num, mode = 'POSTFIX') {
    if (mode === 'COMMAS') {
        return num.toLocaleString(); // Use toLocaleString to format number with commas
    }
    if (mode === 'POSTFIX') {
        let formattedNum;
        let unit = '';
        const sign = num < 0 ? '-' : '';
        const absNum = Math.abs(num);
        if (absNum >= 1e15) {
            throw new Error(`The inputted number is too large (quadrillion and beyond is unsupported)`);
        }
        else if (absNum >= 1e12) {
            formattedNum = Math.floor((absNum * 10) / 1e12) / 10;
            unit = 'T';
        }
        else if (absNum >= 1e9) {
            formattedNum = Math.floor((absNum * 10) / 1e9) / 10;
            unit = 'B';
        }
        else if (absNum >= 1e6) {
            formattedNum = Math.floor((absNum * 10) / 1e6) / 10;
            unit = 'M';
        }
        else if (absNum >= 1e3) {
            formattedNum = Math.floor((absNum * 10) / 1e3) / 10;
            unit = 'K';
        }
        else {
            formattedNum = absNum;
        }
        const formattedStr = formattedNum % 1 === 0 ? formattedNum.toFixed(0) : formattedNum.toFixed(1);
        return sign + formattedStr + unit;
    }
    (0, constructs_1.shouldNeverHappen)(mode);
}
//# sourceMappingURL=data:application/json;base64,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